import React, { useEffect, useState } from 'react'
import checkoutStyles from '../ProductCheckoutPage/styles.module.css'
import pairsWellWithStyles from '../ProductCheckoutPage/PairsWellWith/styles.module.css'
import { useCartStore, selectCart } from '../../../../store/cartStore'
import { useAddToCart } from '../../../hooks/useAddToCart'
import { useNamedPrices } from '../../../NamedPrices'
import useProductOffers from '../../../../hooks/useProductOffers'
import { $neutralGray70, $yellow30 } from '../../../../styles/colors/primitives'
import { CircularProgress } from '@mui/material'
import Text from '../../../Text'
import {
  calculateDiscountFromRegularPriceCents,
  calculatePriceForMultipleShopItems,
  isInCart
} from '../../../../utils/shopItemsAndCart'
import CtaV2 from '../../../CtaV2'
import { addCentsToPrice, subtractPrices } from '../../../../utils/prices'
import CartIcon from '../../../icons/CartIcon'
import ItemSpecifications from '../Utils/ItemSpecifications'
import { findItemByType, getOptionalItems, isItemAvailable, isOutOfStock } from '../../../../utils/shopItems'
import { handleCheckbox } from '../../../../utils/arrays'
import Spacer from '../../../Spacer'
import CheckmarkIcon from '../../../icons/CheckmarkIcon'
import Subtotal from '../ProductCheckoutPage/Subtotal'
import ProductInfoTags from '../ProductCheckoutPage/ProductInfoTags'
import PairsWellWith from '../ProductCheckoutPage/PairsWellWith'
import Carousel from '../ProductCheckoutPage/Carousel'
import SecondaryNavbarAddToCart from '../ProductCheckoutPage/SecondaryNavbarAddToCart'
import Offer from '../ProductCheckoutPage/Offer'
import SimpleBlockContent from '../../../SimpleBlockContent'
import SmartLink from '../../../SmartLink'
import { formatPrice } from '../../../../utils/formats'
import ProductImages from '../ProductCheckoutPage/ProductImages'
import WarningIcon from '../../../icons/WarningIcon'

const cx = (...c) => c.filter(Boolean).join(' ')

function UpgradableWheelBaseCheckoutPage({ title, mainItemTypes, additionalItemTypes, offersText }) {
  // Cart line items now come from the Zustand cart store (Bucket C).
  const shoppingCart = useCartStore(selectCart)
  const { checkoutPageAddToCart } = useAddToCart()

  // namedPrices now come from the NamedPrices abstraction (server-fetched region
  // prices, Overmind fallback isolated in the hook) instead of a direct store
  // read. shopItems/cart still read Overmind here — later migration phases.
  const { namedPricesFetched, ensureRequested: ensureNamedPricesRequested } = useNamedPrices()

  // Shared product-checkout data logic (refetch gate + offer/gallery helpers)
  // lives in useProductOffers so all four checkout forks stay in lockstep (task
  // #95). This page's specifics — a multi-type `mainItems` list, the namedPrices
  // fetch, and the offers built against that list (no displayOnShop/sort) — are
  // kept here.
  // `offers: shopItems` (server-threaded / full-after-refetch offers) replaces the
  // render-time `state.shopItems` reads below (byte-identical value throughout).
  // Cart reads (state.shoppingCart) stay on Overmind (Bucket C). Aliased to
  // `shopItems` to avoid colliding with this page's local `offers`.
  const { getItemOffers, getOfferImages, offers: shopItems, shopItemsFetched } = useProductOffers({ itemType: mainItemTypes[0] })

  const [selectedOfferIndex, setSelectedOfferIndex] = useState(0)
  const [selectedOptionalIndexes, setSelectedOptionalIndexes] = useState([])

  useEffect(() => {
    ensureNamedPricesRequested()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!shopItemsFetched || !namedPricesFetched) {
    return (
      <div className={checkoutStyles.loading} style={{ color: $neutralGray70 }}>
        <CircularProgress color="inherit"/>
      </div>
    )
  }

  const mainItems = mainItemTypes.map(i => findItemByType(i, shopItems))

  const item = mainItems[0]
  const offers = getItemOffers(mainItems[0], mainItems)
  const availableOffers = offers.filter(offer => offer.every(item => item.price && item.available))
  // main offer is the first item in list, it contains all items from product set
  const isMainOfferInCart = offers && offers.length && offers.some(o => o.every(i => isInCart(i, shoppingCart)))
  const optionalItems = getOptionalItems(item, shopItems, false).filter(i => i.productSetId !== item.productSetId)
  const noOffersAvailable = !offers || !offers.length

  const images = getOfferImages(offers[selectedOfferIndex], item)

  const getSelectedItems = () => {
    if (!availableOffers || !availableOffers.length) {
      return []
    }

    // if (isMainOfferInCart) {
      // if main item is already added in cart
      // selected items are - main item + all optional items in cart, so subtotal can be calculated correctly
      // return Array.from(offers[0]).concat(optionalItems.filter(i => isInCart(i, state.shoppingCart)))
    // }

    const selectedMainOffer = offers[selectedOfferIndex]
    const selectedOptionalItems = Array.from(new Set(selectedOptionalIndexes)).flatMap(id => optionalItems[id])

    return Array.from(selectedMainOffer).concat(selectedOptionalItems).filter(o => !!o)
  }

  const getAddToCartCta = (isMainCta) => {
    return {
      title: !isMainOfferInCart ? 'Add to cart' : 'Item added',
      titleIcon: !isMainOfferInCart ? (isMainCta ? <CartIcon large/> : <CartIcon/>) : <CheckmarkIcon/>,
      light: isMainOfferInCart,
      sx: {
        height: isMainCta ? '80px' : null,
        titleTextType: isMainCta ? 'large' : null
      },
      disabled: !getSelectedItems().length,
      onClick: () => {
        // checkoutPageAddToCartClicked already fires the AddToCart pixel + CAPI
        // event WITH value/currency (via o.sendAddToCartMetaPixelEvent). The old
        // extra call here fired a duplicate AddToCart with no price → 13% of
        // AddToCart events had missing currency/value (Meta ROAS flag).
        checkoutPageAddToCart(getSelectedItems().map(i => i.id))
      }
    }
  }

  const getSubtotal = () => {
    return calculatePriceForMultipleShopItems(getSelectedItems())
  }

  const getSubtotalDiscountedCents = () => {
    return calculateDiscountFromRegularPriceCents(getSelectedItems())
  }

  return (
    <>
      <SecondaryNavbarAddToCart
        title={title}
        subtotal={getSubtotal()}
        subtotalBeforeDiscount={addCentsToPrice(getSubtotal(), getSubtotalDiscountedCents())}
        ctaV2={getAddToCartCta(false)}
      />
      <div className={checkoutStyles.wrapper}>
        <div className={cx(checkoutStyles.contentWrapper, images.length > 1 && checkoutStyles.contentWrapperWide)}>
          <div className={checkoutStyles.desktopCarousel}>
            <ProductImages images={images}/>
          </div>
          <div className={checkoutStyles.productInfo}>
            <div className={checkoutStyles.productText}>
              <div className={checkoutStyles.productTitle}>
                <Text type="heading-5">VRS® DirectForce®</Text>
                <Text type="heading-1" primary>{title || item.name}</Text>
              </div>
              <Text type="regular">{item.fullSetDescription || item.description}</Text>
              <SmartLink href="https://vrs.racing/hardware/pro-wheel-base" className={checkoutStyles.linkWrapper}>
                <Text type="regular">Learn more about the VRS DirectForce Pro Wheel Base.</Text>
              </SmartLink>
            </div>
            <div className={checkoutStyles.mobileCarousel}><Carousel images={images}/></div>
            <div className={checkoutStyles.checkout}>
              { (() => {
                if (noOffersAvailable) {
                  return <CtaV2
                    title="Get notified"
                    onClick={() => window.open('https://app.vrs.racing/#/Store', '_blank')}
                    fullWidth
                    light={true}
                  />
                } else if (offers.length === 1) {
                  return null
                } else {
                  return (
                    <div className={checkoutStyles.offers}>
                      { offersText && <><SimpleBlockContent blocks={offersText}/><Spacer size={16}/></> }
                      {
                        offers.map((o, i) => <Offer
                          key={`offer-${i}`}
                          items={o}
                          isSelected={selectedOfferIndex === i}
                          onClick={() => {
                            setSelectedOfferIndex(i)
                          }}
                          isOutOfStock={ !(o.every(item => item.price && item.available)) }
                        />)
                      }
                      <Offer
                        key="fake-offer-20Nm"
                        isSelected={false}
                        items={[{
                          name: 'Upgradable Torque Wheel Base 20Nm',
                          shortConfigInfo: 'Max upgrade level after purchase',
                          price: offers && offers.length && offers[0][0].price ? { priceCents: offers[0][0].price.currency === 'AUD' ? 136400 : 84700, currency: offers[0][0].price.currency } : {}
                        }]}
                        isOutOfStock={true}
                        hideOutOfStockText={true}
                      />
                    </div>
                  )
                }
              }) ()
              }
              <div className={cx(pairsWellWithStyles.pairsWellWithWrapper, !(optionalItems && optionalItems.length) && pairsWellWithStyles.pairsWellWithWrapperHidden)}>
                <div>
                  <Text type="heading-8">Pairs well with</Text>
                  <Spacer size={8}/>
                </div>
                {
                  optionalItems && optionalItems.map((o, i) => <PairsWellWith
                      key={o.name}
                      item={o}
                      isItemInCart={isInCart(o, shoppingCart)}
                      isMainOfferInCart={isMainOfferInCart}
                      onCheckboxClick={() => handleCheckbox(selectedOptionalIndexes, i, (list) => setSelectedOptionalIndexes(list))}
                      isSelected={selectedOptionalIndexes.includes(i)}
                      isAvailable={!isOutOfStock(o)}
                    />
                  )}
              </div>
              {
                noOffersAvailable
                  ? null
                  : (
                    <Subtotal
                      subtotal={getSubtotal()}
                      discountCents={getSubtotalDiscountedCents()}
                      taxIncluded={item && item.taxIncluded}
                      cta={getAddToCartCta(true)}
                    />
                  )}
              { !!item && !!item.importantInfo && <div className="flex gap-1 items-center bg-yellow-100 rounded-lg p-1">
                <WarningIcon color={$yellow30}/>
                <Text type="heading-9">{item.importantInfo}</Text>
              </div>}
              <ProductInfoTags transitTimeDays={null}/>
            </div>
            { item.specs && <ItemSpecifications specs={item.specs} theme={'light'}/> }
          </div>
        </div>
      </div>
    </>
  )
}

export default UpgradableWheelBaseCheckoutPage
